/**
 * Shared empty state component for consistent UI.
 * Canonical location: blocks/empty. Re-exported from ui/empty for backward compatibility.
 */

import React from 'react';
import { FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

export interface EmptyStateProps {
  /** Short title (e.g. "No reports") */
  title?: string;
  /** Description text (required). */
  description: string;
  /** Label for optional action button. */
  actionLabel?: string;
  /** Called when action button is clicked. */
  onAction?: () => void;
  /** Optional custom action node (use actionLabel/onAction for standard button). */
  action?: React.ReactNode;
  /** Optional icon (defaults to FileText). */
  icon?: React.ReactNode;
  className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  actionLabel,
  onAction,
  action,
  icon,
  className,
}) => {
  const defaultIcon = icon ?? (
    <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
  );
  const actionContent =
    actionLabel != null && onAction != null ? (
      <Button variant="outline" size="sm" onClick={onAction}>
        {actionLabel}
      </Button>
    ) : action;

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-12 px-4 text-center',
        className
      )}
    >
      <div className="max-w-md">
        {defaultIcon}
        {title && (
          <h3 className="text-lg font-semibold mb-2 text-foreground">
            {title}
          </h3>
        )}
        <p className="text-sm text-muted-foreground mb-4">
          {description}
        </p>
        {actionContent && <div className="mt-2">{actionContent}</div>}
      </div>
    </div>
  );
};
